<template>
  <form @submit.prevent="reply">
    <Textarea
      v-model="response"
      name="response"
      rows="3"
      :placeholder="$t('pages.review.reviewPage.items.yourReply')"
      class="my-2 w-full xl:w-1/2"
    />
    <ReviewTemplateSelector
      :review="review"
      @template-selected="setResponseTemplate"
      @variable-used="setVariableUsed"
    />
    <div class="mt-2 flex flex-col items-center space-x-0 sm:flex-row sm:space-x-2 md:flex-row">
      <Button
        type="submit"
        variant="default-primary"
        class="mt-2 w-full md:w-auto"
        :disabled="!response || isProcessing"
      >
        <Loader2 v-if="isProcessing" class="animate-spin" />
        {{ $t('pages.review.reviewPage.items.buttons.publishReply') }}
      </Button>
      <Button class="mt-2 w-full md:w-auto" variant="outline" @click="$emit('cancel')">
        {{ $t('pages.review.reviewPage.items.buttons.cancel') }}
      </Button>
    </div>
  </form>
</template>

<script>
import { Button } from '@/ui/button'
import { Loader2 } from 'lucide-vue-next'
import ReviewTemplateSelector from './ReviewTemplateSelector.vue'
import useReview from '@/composables/review'

export default {
  components: {
    Button,
    Loader2,
    ReviewTemplateSelector
  },
  props: {
    review: {
      type: Object,
      required: true
    }
  },

  emits: ['reply', 'cancel'],
  setup() {
    const notification = useNotification()
    const { transformOwnerResponse } = useReview()
    return {
      notification,
      transformOwnerResponse
    }
  },
  data: function () {
    return {
      response: '',
      isProcessing: false
    }
  },
  mounted() {
    if (this.review.ownerResponseText) {
      this.response = this.review.ownerResponseText
    }
  },
  methods: {
    reply() {
      if (!this.response.length) {
        this.notification.create('error', {
          text: this.$t('pages.review.reviewReplyForm.errors.responseRequired'),
          persist: true
        })
        return
      }
      this.isProcessing = true
      const ownerResponseText = this.transformOwnerResponse(this.response, this.review)
      if (this.review.ownerResponseText) {
        this.axios
          .put(`/reviews/${this.review.id}`, {
            response: ownerResponseText
          })
          .then(() => {
            this.notification.create('success', {
              text: this.$t('pages.review.reviewReplyForm.success.modifiedReply')
            })
            this.$emit('reply', ownerResponseText)
          })
          .catch(() => {
            this.notification.create('error', {
              text: this.$t('pages.review.reviewReplyForm.error.modifiedReply'),
              persist: true
            })
          })
          .finally(() => {
            this.isProcessing = false
          })
      } else {
        this.axios
          .post(`/reviews/${this.review.id}/reply`, {
            response: ownerResponseText
          })
          .then(() => {
            this.notification.create('success', {
              text: this.$t('pages.review.reviewReplyForm.success.replied')
            })
            this.$emit('reply', ownerResponseText)
          })
          .catch(() => {
            this.notification.create('error', {
              text: this.$t('pages.review.reviewReplyForm.error.replied'),
              persist: true
            })
          })
          .finally(() => {
            this.isProcessing = false
          })
      }
    },
    setResponseTemplate(template) {
      this.response = template.content
    },
    setVariableUsed(text) {
      this.response += ` ${text}`
    }
  }
}
</script>
